import { useEffect, useState } from "react";
import FinanceLayout from "@/components/FinanceLayout";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Camera, PlusCircle, Pencil, Trash2, X, Save, Mail, Phone, User, Link2, Copy, Ban } from "lucide-react";

const inp = "w-full h-11 rounded-lg border border-slate-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-violet-100 focus:border-violet-500";

function EditModal({ ph, onClose, onSaved }) {
  const isNew = !ph?.id;
  const [f, setF] = useState({
    first_name: ph?.first_name || "",
    last_name: ph?.last_name || "",
    phone: ph?.phone || "",
    email: ph?.email || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name.trim()) return setError("Ad zorunlu");
    if (!f.email.trim()) return setError("E-posta zorunlu");
    setSaving(true);
    try {
      if (isNew) await onSaved(f, true); else await onSaved(f, false);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Kaydedilemedi"));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center px-6" data-testid="ph-dialog">
      <div className="bg-white rounded-xl max-w-[520px] w-full p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold text-slate-900">{isNew ? "Yeni Çekimci" : (ph.is_staff ? "Staf Bilgileri" : "Çekimci Düzenle")}</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5"><User size={12} /> Ad</label>
              <input value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} className={inp} data-testid="ph-first" /></div>
            <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Soyad</label>
              <input value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} className={inp} data-testid="ph-last" /></div>
          </div>
          <div><label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5"><Phone size={12} /> Telefon</label>
            <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inp} data-testid="ph-phone" /></div>
          <div><label className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1.5"><Mail size={12} /> E-posta *</label>
            <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} required data-testid="ph-email" />
            <p className="text-[11px] text-slate-400 mt-1">Onay/İptal bildirimi bu adrese gönderilir.</p></div>
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-full border border-slate-200 px-5 py-2 text-sm">İptal</button>
            <button type="submit" disabled={saving} data-testid="ph-save"
              className="rounded-full bg-violet-700 hover:bg-violet-600 text-white px-5 py-2 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
              <Save size={13} /> {saving ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Photographers() {
  const { workspace } = useWorkspace();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const { data } = await api.get("/photographers", { params: { workspace } });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace]);

  const saveNew = async (f) => {
    await api.post("/photographers", { workspace, ...f });
    toast.success("Çekimci eklendi");
    setCreating(false); load();
  };
  const saveEdit = async (f) => {
    await api.put(`/photographers/${editing.id}`, f);
    toast.success("Çekimci güncellendi");
    setEditing(null); load();
  };
  const remove = async (ph) => {
    if (ph.is_staff) { toast.error("Staf silinemez, düzenleyebilirsiniz"); return; }
    if (!window.confirm(`${ph.first_name} ${ph.last_name} silinsin mi? Bağlı çekimler Staf'a devredilir.`)) return;
    try {
      await api.delete(`/photographers/${ph.id}`);
      toast.success("Çekimci silindi");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, "Silinemedi")); }
  };

  const generateShareLink = async (ph) => {
    try {
      const { data } = await api.post(`/photographers/${ph.id}/share-link`);
      const url = `${window.location.origin}/public/photographer/${data.share_token}`;
      try { await navigator.clipboard.writeText(url); toast.success("Link panoya kopyalandı"); }
      catch { toast.success("Link oluşturuldu"); }
      window.prompt(`${ph.first_name} için paylaşım linki (Ctrl+C ile kopyalayın):`, url);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, "Link oluşturulamadı")); }
  };

  const revokeShareLink = async (ph) => {
    if (!window.confirm(`${ph.first_name} için mevcut paylaşım linkini iptal etmek istiyor musunuz? Link erişimi hemen kesilir.`)) return;
    try {
      await api.delete(`/photographers/${ph.id}/share-link`);
      toast.success("Link iptal edildi");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, "İptal edilemedi")); }
  };

  return (
    <FinanceLayout>
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <Camera size={28} className="text-violet-700" /> Çekimciler
          </h1>
          <p className="text-sm text-slate-500 mt-2">Staf ve dış çekimcileri buradan yönetin. E-posta adresi onay/iptal bildirimleri için kullanılır.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="ph-new"
          className="inline-flex items-center gap-2 rounded-full bg-violet-700 hover:bg-violet-600 text-white px-5 py-2.5 text-sm font-semibold">
          <PlusCircle size={14} /> Yeni Çekimci
        </button>
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/40">
              {["Ad Soyad", "Telefon", "E-posta", "Rol", "Paylaşım Linki", "İşlem"].map((h) => (
                <th key={h} className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="py-14 text-center text-sm text-slate-500">Çekimci yok.</td></tr>
            ) : items.map((r) => (
              <tr key={r.id} className="border-b border-slate-50 last:border-none hover:bg-slate-50/40" data-testid={`ph-row-${r.id}`}>
                <td className="py-4 px-6 text-sm font-medium text-slate-900">{r.first_name} {r.last_name}</td>
                <td className="py-4 px-6 text-sm text-slate-700">{r.phone || "—"}</td>
                <td className="py-4 px-6 text-sm text-slate-700">{r.email}</td>
                <td className="py-4 px-6">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border ${r.is_staff ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                    {r.is_staff ? "Staf" : "Dış Çekimci"}
                  </span>
                </td>
                <td className="py-4 px-6">
                  {r.share_token ? (
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => {
                        const url = `${window.location.origin}/public/photographer/${r.share_token}`;
                        navigator.clipboard.writeText(url).then(() => toast.success("Link panoya kopyalandı"));
                        window.prompt("Paylaşım linki:", url);
                      }} data-testid={`ph-link-copy-${r.id}`}
                        className="inline-flex items-center gap-1.5 rounded-md bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 px-2.5 py-1 text-xs font-medium">
                        <Copy size={11} /> Linki Kopyala
                      </button>
                      <button onClick={() => revokeShareLink(r)} data-testid={`ph-link-revoke-${r.id}`} title="Linki İptal Et"
                        className="w-7 h-7 rounded-md hover:bg-red-50 text-red-500 inline-flex items-center justify-center">
                        <Ban size={12} />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => generateShareLink(r)} data-testid={`ph-link-gen-${r.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 hover:bg-slate-50 px-2.5 py-1 text-xs text-slate-600">
                      <Link2 size={11} /> Link Oluştur
                    </button>
                  )}
                </td>
                <td className="py-4 px-6">
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => setEditing(r)} title="Düzenle" data-testid={`ph-edit-${r.id}`}
                      className="w-8 h-8 rounded-md hover:bg-slate-100 text-slate-600 inline-flex items-center justify-center"><Pencil size={14} /></button>
                    {!r.is_staff && (
                      <button onClick={() => remove(r)} title="Sil" data-testid={`ph-del-${r.id}`}
                        className="w-8 h-8 rounded-md hover:bg-red-50 text-red-500 inline-flex items-center justify-center"><Trash2 size={14} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <EditModal onClose={() => setCreating(false)} onSaved={saveNew} />}
      {editing && <EditModal ph={editing} onClose={() => setEditing(null)} onSaved={saveEdit} />}
    </FinanceLayout>
  );
}
